import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    output,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { assign as radashAssign } from 'radash';

import { BaseChartComponent } from '@connect-services/client/components/statistics/base-chart/base-chart.component';
import { ChartLayoutComponent } from '@connect-services/client/components/statistics/chart-layout/chart-layout.component';
import {
    BaseChartOptions,
    ChartComponentInterface,
    collapsePieGaugeHeightChart,
    commonOptions,
    expandedPieGaugeHeightChart,
} from '@connect-services/client/services/chart.service';
import { ComputedStatistic, PieComputedStatisticResult } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [BaseChartComponent, ChartLayoutComponent, RessourcesModule],
    selector: 'app-pie-chart',
    templateUrl: './pie-chart.component.html',
})
export class PieChartComponent implements ChartComponentInterface {
    public readonly computedStatistic = input.required<ComputedStatistic>();
    public readonly isExpanded = input.required<boolean>();
    public readonly isPrintScreen = input<boolean>(false);
    public readonly legendClickCategory = signal('');
    public readonly legendClickColor = signal('');
    public readonly legendClickValue = signal('');
    public readonly legendClickValuePercent = signal('');
    public readonly options = computed<BaseChartOptions>(() => {
        const computedStatistic = this.computedStatistic();
        const data: PieComputedStatisticResult[] =
            (computedStatistic.result?.data as PieComputedStatisticResult[]) || [];

        const seriesAndLabels = data.reduce(
            (acc: { labels: string[]; series: number[] }, value) => {
                acc.series.push(Number(value.x));
                acc.labels.push(value.label || '');

                return acc;
            },
            { labels: [], series: [] },
        );

        return radashAssign(commonOptions, {
            chart: {
                animations: {
                    enabled: !this.isPrintScreen(),
                },
                events: {
                    mounted: () => {
                        this.ready.emit();
                    },
                },
                height: this.isExpanded() ? expandedPieGaugeHeightChart : collapsePieGaugeHeightChart,
                type: 'pie',
                width: this.isExpanded() ? expandedPieGaugeHeightChart : collapsePieGaugeHeightChart,
            },
            colors: computedStatistic.renderOptions.colors ?? [],
            dataLabels: {
                enabled: this.isPrintScreen(),
            },
            fill: {
                colors: computedStatistic.renderOptions.colors ?? [],
            },
            labels: seriesAndLabels.labels,
            legend: {
                show: false,
            },
            plotOptions: { pie: { expandOnClick: true } },
            series: seriesAndLabels.series,
            states: {
                hover: {
                    filter: {
                        type: 'darken',
                    },
                },
            },
            tooltip: {
                fillSeriesColor: false,
                fixed: {
                    enabled: false,
                },
                followCursor: true,
                marker: {
                    show: false,
                },
                y: {
                    formatter: (val, opts): string => {
                        const percentage = (
                            (val / opts.config.series.reduce((count: number, value: number) => count + value, 0)) *
                            100
                        ).toFixed(1);

                        return `${val} (${percentage}%)`;
                    },
                },
            },
        });
    });

    public readonly pieData = computed(() => {
        return (this.computedStatistic().result?.data as PieComputedStatisticResult[]) || [];
    });

    public ready = output<void>();
    public readonly totalPieValue = computed(() => {
        return this.pieData().reduce((sum, item) => sum + (item.x ?? 0), 0);
    });

    public constructor() {
        effect(() => {
            if (!this.totalPieValue()) {
                this.ready.emit();
            }
        });
    }

    public getLegendColor(item: PieComputedStatisticResult): string {
        const index = this.pieData().findIndex((index: PieComputedStatisticResult) => index.label === item.label);

        return this.computedStatistic().renderOptions?.colors?.[index] ?? '#cccccc';
    }

    public isSelected(item: PieComputedStatisticResult): boolean {
        return this.legendClickCategory() === (item.label ?? '');
    }

    public onLegendClick(item: PieComputedStatisticResult): void {
        const label = item.label ?? '';
        const current = this.legendClickCategory();

        if (current === label) {
            this.clearLegendSelection();

            return;
        }

        const valueNum = Number(item.x ?? 0);
        const totalNum = this.pieData().reduce((sum, data: PieComputedStatisticResult) => sum + Number(data.x ?? 0), 0);
        const percentStr = totalNum ? `${((valueNum / totalNum) * 100).toFixed(1)}%` : '0.0%';

        this.legendClickCategory.set(label);
        this.legendClickColor.set(this.getLegendColor(item));
        this.legendClickValue.set(String(valueNum));
        this.legendClickValuePercent.set(percentStr);
    }

    private clearLegendSelection(): void {
        this.legendClickValue.set('');
        this.legendClickCategory.set('');
        this.legendClickColor.set('');
        this.legendClickValuePercent.set('');
    }
}
